import { useTranslation } from '@/hooks/useTranslation';
import { Badge } from '@/components/ui/badge';
import { getJurisdictionIdentityConfig } from '@/config/jurisdictionIdentity';
import type { OrganizationBusinessIdentity } from '@/hooks/useOrganizationBusinessIdentity';
import type { BusinessVendorIdentity } from '@/hooks/useBusinessVendorIdentity';

interface IdentityFieldProps {
  label: string;
  value: string | null | undefined;
}

const IdentityField = ({ label, value }: IdentityFieldProps) => {
  if (!value) return null;
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <p className="text-sm">{value}</p>
    </div>
  );
};

interface OrganizationIdentityDisplayProps {
  orgIdentity: OrganizationBusinessIdentity | null | undefined;
  vatNumber?: string | null;
  orgCountry?: string | null;
  taxJurisdiction?: string | null;
  showBanking?: boolean;
}

export const OrganizationIdentityDisplay = ({
  orgIdentity,
  vatNumber,
  orgCountry,
  taxJurisdiction,
  showBanking = true,
}: OrganizationIdentityDisplayProps) => {
  const { t } = useTranslation('common');

  if (!orgIdentity) return null;

  const regCountry = orgIdentity.legal_forms?.country_code ?? orgCountry;
  const regConfig = getJurisdictionIdentityConfig(regCountry);
  const vatConfig = getJurisdictionIdentityConfig(taxJurisdiction ?? regCountry);

  const regLabel = t(regConfig.registrationNumber.labelKey, regConfig.registrationNumber.label);
  const vatLabel = t(vatConfig.vatNumber.labelKey, vatConfig.vatNumber.label);
  const taxEntityLabel = regConfig.taxEntityNumber
    ? t(regConfig.taxEntityNumber.labelKey, regConfig.taxEntityNumber.label)
    : null;
  const taxExemptLabel = regConfig.taxExempt
    ? t(regConfig.taxExempt.labelKey, regConfig.taxExempt.label)
    : t('businessIdentity.taxExempt', 'Tax Exempt');

  const address = orgIdentity.postal_address ?? orgIdentity.registered_address;

  const hasContactInfo = Boolean(address || orgIdentity.contact_email);
  const hasFormalIdentity = Boolean(
    orgIdentity.registration_number ||
      vatNumber ||
      orgIdentity.tax_entity_number ||
      (showBanking && orgIdentity.iban) ||
      orgIdentity.is_tax_exempt,
  );

  if (!hasContactInfo && !hasFormalIdentity) return null;

  return (
    <div className="space-y-1">
      {address && <p className="text-sm whitespace-pre-line">{address}</p>}
      {orgIdentity.contact_email && <p className="text-sm">{orgIdentity.contact_email}</p>}
      {hasContactInfo && hasFormalIdentity && (
        <div className="border-t border-border mt-2 pt-2" />
      )}
      <IdentityField label={regLabel} value={orgIdentity.registration_number} />
      <IdentityField label={vatLabel} value={vatNumber} />
      {taxEntityLabel && (
        <IdentityField label={taxEntityLabel} value={orgIdentity.tax_entity_number} />
      )}
      {showBanking && (
        <>
          <IdentityField label={t('businessIdentity.iban', 'IBAN')} value={orgIdentity.iban} />
          <IdentityField label={t('businessIdentity.bic', 'BIC')} value={orgIdentity.bic} />
        </>
      )}
      {orgIdentity.is_tax_exempt && (
        <Badge variant="secondary" className="text-xs mt-1">
          {taxExemptLabel}
        </Badge>
      )}
    </div>
  );
};

interface VendorIdentityDisplayProps {
  vendorIdentity: BusinessVendorIdentity | null | undefined;
}

export const VendorIdentityDisplay = ({ vendorIdentity }: VendorIdentityDisplayProps) => {
  const { t } = useTranslation('common');

  if (!vendorIdentity) return null;

  const config = getJurisdictionIdentityConfig(vendorIdentity.country_code);
  const regLabel = t(config.registrationNumber.labelKey, config.registrationNumber.label);
  const vatLabel = t(config.vatNumber.labelKey, config.vatNumber.label);

  const hasAnyField =
    vendorIdentity.trading_name ||
    vendorIdentity.registration_number ||
    vendorIdentity.vat_number ||
    vendorIdentity.iban ||
    vendorIdentity.is_related_party;

  if (!hasAnyField) return null;

  return (
    <div className="mt-2 pt-2 border-t border-border space-y-1">
      <IdentityField label={t('businessIdentity.tradingName', 'Trading Name')} value={vendorIdentity.trading_name} />
      <IdentityField label={regLabel} value={vendorIdentity.registration_number} />
      <IdentityField label={vatLabel} value={vendorIdentity.vat_number} />
      <IdentityField label={t('businessIdentity.iban', 'IBAN')} value={vendorIdentity.iban} />
      <IdentityField label={t('businessIdentity.bic', 'BIC')} value={vendorIdentity.bic} />
      {vendorIdentity.is_related_party && (
        <Badge variant="outline" className="text-xs mt-1">
          {t('businessIdentity.relatedParty', 'Related Party')}
        </Badge>
      )}
    </div>
  );
};
